import React, {  useEffect  } from 'react';
import {  useSurvey  } from './index';
import {  SurveyViewer  } from './index';
import {  BounceLoader  } from '../loader';
import {  SpendingHabitsIcon, InvestmentGoalsIcon, FinancialStatusIcon, UserDemographicsIcon, TrophyIcon, SmileyFaceIcon } from '../icons';
import {  useHeader  } from '../header';
import {  formatCurrency  } from '../common/utils';

const SURVEY_ICONS: Record<string, React.ReactNode> = {
    'Spending Habits': <SpendingHabitsIcon />,
    'Investing Goals And Experience': <InvestmentGoalsIcon />,
    'Financial Status': <FinancialStatusIcon />,
    'Demographics': <UserDemographicsIcon />,
};

const SurveyPage = () => {
    const { setHeaderTitle, setHeaderSubTitle } = useHeader();
    const { surveys, activeSurvey, setActiveSurvey, completedIds, answers, loading } = useSurvey();

    const visibleSurveys = surveys.filter(s => !completedIds.has(s.id));
    const [heroSurvey, ...restOfSurveys] = visibleSurveys;

    useEffect(() => {
        setHeaderTitle('Survey');
        setHeaderSubTitle('Earn rewards for every survey you complete.');
    });

    const getAnsweredCount = (surveyId: string) => (answers[surveyId] ?? []).length;

    const getHeroTag = () => {
        if (!heroSurvey) return '';
        const count = getAnsweredCount(heroSurvey.id);
        if (count === 0) return 'Up next';
        return `In progress · ${ count + 1 } of ${ heroSurvey.questions.length }`;
    };

    const noSurveysAtAll = surveys.length === 0;

    return (
        <div className="light-mode survey-page">
            { loading ? (
                <BounceLoader title="Loading surveys" subTitle="Fetching your surveys..." />
            ) : activeSurvey ? (
                <SurveyViewer />
            ) : (
                <>
                    <div className="survey-rewards-banner">
                        <div className="survey-rewards-icon">
                            <TrophyIcon />
                        </div>

                        <div className="survey-rewards-text">
                            <div className="survey-rewards-title">Complete surveys, earn rewards</div>
                            <div className="survey-rewards-sub">Every survey you finish adds rewards to your wallet</div>
                        </div>
                    </div>

                    <div className="survey-page-divider" />

                    { visibleSurveys.length === 0 ? (
                        <div className="survey-empty">
                            <div className="survey-empty-icon">
                                <FinancialStatusIcon />
                            </div>
                            <div className="survey-empty-title">
                                { noSurveysAtAll ? 'No surveys available right now' : 'All done — thanks for sharing' }
                            </div>
                            <p className="survey-empty-sub">
                                { noSurveysAtAll
                                    ? 'Check back later — new surveys will appear here.'
                                    : "You've completed all available surveys. Your rewards will be added to your wallet." }
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="survey-hero" onClick={() => setActiveSurvey(heroSurvey)}>
                                <div className="survey-hero-icon">
                                    { SURVEY_ICONS[heroSurvey.title] ?? <SmileyFaceIcon /> }
                                </div>
                                <div className="survey-hero-info">
                                    <div className="survey-hero-top">
                                        <div className="survey-hero-tag">{ getHeroTag() }</div>
                                        <span className="survey-hero-reward">{ formatCurrency(heroSurvey.rewardAmount) } reward</span>
                                    </div>
                                    <div className="survey-hero-title">{ heroSurvey.title }</div>
                                    <div className="survey-hero-sub">{ heroSurvey.description }</div>
                                    <button className="survey-hero-btn">
                                        { getAnsweredCount(heroSurvey.id) > 0 ? 'Continue →' : 'Start →' }
                                    </button>
                                </div>
                            </div>

                            { restOfSurveys.length > 0 && (
                                <div className="survey-list">
                                    { restOfSurveys.map(survey => (
                                        <div
                                            key={survey.id}
                                            className="survey-row"
                                            onClick={() => setActiveSurvey(survey)}
                                        >
                                            <div className="survey-row-icon">
                                                { SURVEY_ICONS[survey.title] ?? <SmileyFaceIcon /> }
                                            </div>
                                            <div className="survey-row-title">{ survey.title }</div>
                                            <span className="survey-row-reward">{ formatCurrency(survey.rewardAmount) }</span>
                                            <span className="survey-row-count">{ survey.questions.length } questions</span>
                                            <i className="ti ti-arrow-right survey-row-arrow" aria-hidden="true" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default SurveyPage;